import { createReducer } from 'utils/redux';
import { API, get, post, put } from 'utils/api';
import { __t, applyTranslations } from 'utils/language';
import { logError } from 'utils/log';
import { getMissingImagesValidationError } from 'utils/profile';

import { hideLoading, showLoading } from 'redux-store/reducers/loading-reducer';
import { RESET_ALL as RESET_ON_LOGOUT, danger, notification, success } from 'redux-store/reducers/common-reducer';
import { fetchCurrentAccount } from '../../access/reducers/account';
import { hideConfirmModal, showConfirmModal } from 'redux-store/reducers/modal-reducer';
import { organizationsListUpdated } from 'modules/organizations/reducers/organizations';

import {
  trackEventProfileCoverImageUploaded,
  trackEventProfileCreated,
  trackEventProfileImageUploaded,
  trackEventProfileUpdated
} from '../../tracking/utils/tracking';

import type { BrandProfileReduxState } from 'types';

applyTranslations(require('./brand-profile-reducer.i18n.json'));
applyTranslations(require('locales/common/collabary-common.i18n.json'));

const RECEIVE = 'brandProfile/RECEIVE';
const RESET = 'brandProfile/RESET';

const { BRANDS } = API.END_POINTS;
const { IMAGES } = API.END_POINTS.API;

const initialState: BrandProfileReduxState = {
  profile: {}
};

export function resetBrandProfile() {
  return {
    type: RESET,
    payload: {}
  };
}

function receiveBrandProfile(data) {
  return {
    type: RECEIVE,
    payload: {
      data
    }
  };
}

export function uploadImage(brandProfile, type, image) {
  const url = IMAGES;

  return (dispatch) => {
    if (!image.file) {
      return Promise.resolve(image);
    }

    dispatch(showLoading(url));

    const data = new FormData();

    data.append('file', image.file);

    return post(url, { data })
      .then((response) => {
        const createdImage = {
          area: image.area,
          ...response.data
        };

        if (type === 'cover') {
          trackEventProfileCoverImageUploaded('brand');
        } else {
          trackEventProfileImageUploaded('brand');
        }

        dispatch(hideLoading(url));

        return createdImage;
      })
      .catch((error) => {
        const errorMessage = __t(type === 'cover'
          ? 'brand_profile_reducer.cover_image_upload_error'
          : 'brand_profile_reducer.profile_image_upload_error'
        );

        dispatch(showConfirmModal({
          message: errorMessage,
          confirmButtonLabel: __t('collabary_common.action_button.ok'),
          onConfirm: () => dispatch(hideConfirmModal())
        }));

        dispatch(hideLoading(url));
        logError(error);

        throw error;
      });
  };
}

function sendBrandProfile(url, method, profileDetails) {
  const requestMethod = method === 'create' ? post : put;
  const trackingMethod = method === 'create' ? trackEventProfileCreated : trackEventProfileUpdated;

  return (dispatch) => {
    const missingImagesError = getMissingImagesValidationError(profileDetails.coverImage) || getMissingImagesValidationError(profileDetails.profileImage);

    if (missingImagesError) {
      dispatch(danger(missingImagesError));
      return Promise.reject(missingImagesError);
    }

    dispatch(showLoading(url));

    return requestMethod(url, { data: profileDetails })
      .then((response) => {
        const brandProfile = response.data;
        dispatch(hideLoading(url));
        dispatch(success(__t('brand_profile_reducer.save_brand_profile_success'), { keepBetweenPages: true }));
        trackingMethod('brand');

        return dispatch(fetchCurrentAccount()) // it will load updated currentOrganization.brandProfiles
          .then(account => dispatch(organizationsListUpdated(account.organizations)))
          .then(() => brandProfile);
      })
      .catch((error) => {
        dispatch(hideLoading(url));
        if (error.response) {
          dispatch(danger(__t('brand_profile_reducer.save_brand_profile_error'), { keepBetweenPages: true }));
        }

        throw error;
      });
  };
}

export function createBrandProfile(profileDetails, organizationId) {
  return (dispatch) => {
    const url = BRANDS.createBrandProfile(organizationId);

    return dispatch(sendBrandProfile(url, 'create', profileDetails));
  };
}

export function saveBrandProfile(profileId, profileDetails) {
  return (dispatch) => {
    const url = BRANDS.updateBrandProfile(profileId);

    return dispatch(sendBrandProfile(url, 'update', profileDetails));
  };
}

export function fetchBrandProfile(profileId, showLoader = true, showErrorPage = false) {
  return (dispatch) => {
    const url = BRANDS.getBrandProfile(profileId);

    if (showLoader) {
      dispatch(showLoading(url));
    }
    dispatch(resetBrandProfile());

    return get(url)
      .then((response) => {
        const brandProfile = response.data;

        dispatch(receiveBrandProfile(brandProfile));

        if (showLoader) {
          dispatch(hideLoading(url));
        }

        return brandProfile;
      })
      .catch((err) => {
        if (showLoader) {
          dispatch(hideLoading(url));
        }

        if (showErrorPage) {
          const response = err.response || {};
          dispatch(notification({
            status: response.status || 500
          }));
        }

        return {};
      });
  };
}

export default createReducer<BrandProfileReduxState, any>('brandProfile', initialState, {
  [RESET_ON_LOGOUT]: () => initialState,
  [RESET]: () => initialState,
  [RECEIVE]: (state, { data }) => ({
    ...state,
    profile: data
  })
});
